import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Input } from '../../../components/ui';
import StatusChip from './ui/StatusChip';
import {
  buildWabaProfileForm,
  buildWabaProfileUpdatePayload,
  getWabaProfileDisplayFields,
  validateWabaProfileForm,
  WABA_EDITABLE_FIELDS,
  WABA_READ_ONLY_FIELDS,
} from '../utils/wabaProfileUtils';

const APP_TYPES = ['whatsapp', 'gupshup', 'trustsignal'];

export default function WabaProfilePanel({
  app,
  canEdit,
  isSaving,
  onSave,
  styles,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(() => buildWabaProfileForm(app));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isEditing) {
      setForm(buildWabaProfileForm(app));
      setErrors({});
    }
  }, [app, isEditing]);

  const displayFields = useMemo(() => getWabaProfileDisplayFields(app), [app]);
  const isActive = app.is_active === true || app.status === 'active';

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleCancel = () => {
    setForm(buildWabaProfileForm(app));
    setErrors({});
    setIsEditing(false);
  };

  const handleSave = async () => {
    const validationErrors = validateWabaProfileForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    const payload = buildWabaProfileUpdatePayload(form, app);
    const saved = await onSave?.(payload);
    if (saved) {
      setIsEditing(false);
      setForm(buildWabaProfileForm(saved));
    }
  };

  return (
    <Box sx={styles.wabaProfileCard}>
      <Box sx={styles.wabaProfileHeader}>
        <Box>
          <Typography variant="h5" sx={styles.wabaProfileTitle}>
            WABA Details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isEditing
              ? 'Update your WhatsApp Business app configuration.'
              : 'These are your WABA account details approved by WhatsApp.'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StatusChip value={isActive ? 'active' : 'inactive'} label={isActive ? 'Live' : 'Not Live'} />
          {canEdit && !isEditing ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditOutlinedIcon />}
              onClick={() => setIsEditing(true)}
              sx={styles.syncButton}
            >
              Edit
            </Button>
          ) : null}
        </Box>
      </Box>

      <Divider sx={{ my: 2.5 }} />

      {isEditing ? (
        <>
          <Box sx={styles.wabaProfileFormGrid}>
            {WABA_EDITABLE_FIELDS.filter(({ key }) => key !== 'app_type').map(({ key, label, required, digitsOnly, secret, placeholder }) => (
              <Input
                key={key}
                label={label}
                required={required}
                type={secret ? 'password' : 'text'}
                placeholder={placeholder}
                value={form[key] ?? ''}
                onChange={(event) => {
                  const nextValue = digitsOnly
                    ? event.target.value.replace(/\D/g, '')
                    : event.target.value;
                  updateField(key, nextValue);
                }}
                error={Boolean(errors[key])}
                errorMessage={errors[key]}
              />
            ))}

            {WABA_READ_ONLY_FIELDS.map(({ key, label }) => (
              app[key] ? (
                <Input
                  key={key}
                  label={label}
                  value={String(app[key])}
                  // readOnly
                />
              ) : null
            ))}

            <TextField
              select
              size="small"
              label="App Type"
              value={form.app_type || 'whatsapp'}
              onChange={(event) => updateField('app_type', event.target.value)}
              sx={{ width: '100%' }}
            >
              {APP_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Switch
                checked={Boolean(form.is_active)}
                onChange={(event) => updateField('is_active', event.target.checked)}
              />
            }
            label="App is live"
          />

          <Box sx={styles.wabaProfileActions}>
            <Button variant="outlined" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
              sx={styles.createButton}
            >
              {isSaving ? 'Saving…' : 'Save Changes'}
            </Button>
          </Box>
        </>
      ) : (
        <Box sx={styles.wabaProfileDisplayGrid}>
          {displayFields.map(({ label, value }) => (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                {String(value)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

WabaProfilePanel.propTypes = {
  app: PropTypes.object.isRequired,
  canEdit: PropTypes.bool,
  isSaving: PropTypes.bool,
  onSave: PropTypes.func,
  styles: PropTypes.object.isRequired,
};
